import { useState } from 'react';

import { CloseRounded, Search, Tune } from '@mui/icons-material';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { TimelineFilterOptions } from '../services';
import {
  EMPTY_TIMELINE_FILTERS,
  TimelineFilters,
  TimelineSortOption,
  TimelineTab,
} from '../timelineData';

interface TimelineToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  activeTab: TimelineTab;
  sort: TimelineSortOption;
  onSortChange: (sort: TimelineSortOption) => void;
  appliedFilters: TimelineFilters;
  onApplyFilters: (filters: TimelineFilters) => void;
  onClearAll: () => void;
  filterOptions?: TimelineFilterOptions;
  filterOptionsLoading: boolean;
  filterOptionsError: boolean;
  onRetryFilterOptions: () => void;
  canFilterFavorites: boolean;
  navbarAdditionalHeight: number;
  operationScoped?: boolean;
}

const FILTER_FIELDS: Array<{
  name:
    | 'markets'
    | 'categories'
    | 'types'
    | 'species'
    | 'themes'
    | 'relevances';
  label: string;
}> = [
  { name: 'markets', label: 'Mercados' },
  { name: 'categories', label: 'Categorias' },
  { name: 'types', label: 'Tipos de documento' },
  { name: 'species', label: 'Espécies' },
  { name: 'themes', label: 'Assuntos' },
  { name: 'relevances', label: 'Relevância' },
];

const copyFilters = (
  filters: TimelineFilters,
  operationScoped = false,
): TimelineFilters => ({
  ...filters,
  markets: operationScoped ? [] : [...filters.markets],
  categories: [...filters.categories],
  types: [...filters.types],
  species: [...filters.species],
  themes: [...filters.themes],
  relevances: [...filters.relevances],
  favorites: operationScoped ? false : filters.favorites,
});

const countSelectedFilters = (
  filters: TimelineFilters,
  operationScoped = false,
) =>
  (operationScoped ? 0 : filters.markets.length) +
  filters.categories.length +
  filters.types.length +
  filters.species.length +
  filters.themes.length +
  filters.relevances.length +
  Number(Boolean(filters.dateStart)) +
  Number(Boolean(filters.dateEnd)) +
  Number(!operationScoped && filters.favorites);

const hasSelectedFilters = (
  filters: TimelineFilters,
  operationScoped = false,
) => countSelectedFilters(filters, operationScoped) > 0;

export default function TimelineToolbar({
  searchValue,
  onSearchChange,
  activeTab,
  sort,
  onSortChange,
  appliedFilters,
  onApplyFilters,
  onClearAll,
  filterOptions,
  filterOptionsLoading,
  filterOptionsError,
  onRetryFilterOptions,
  canFilterFavorites,
  navbarAdditionalHeight,
  operationScoped = false,
}: TimelineToolbarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(() =>
    copyFilters(appliedFilters, operationScoped),
  );
  const visibleFilterFields = operationScoped
    ? FILTER_FIELDS.filter(({ name }) => name !== 'markets')
    : FILTER_FIELDS;
  const hasInvalidDateRange =
    Boolean(draftFilters.dateStart && draftFilters.dateEnd) &&
    draftFilters.dateStart > draftFilters.dateEnd;
  const clearIsDisabled =
    !hasSelectedFilters(draftFilters, operationScoped) && !searchValue.trim();
  const mobileNavbarHeight = 150 + navbarAdditionalHeight;
  const largerNavbarHeight = 75 + navbarAdditionalHeight;

  const openDrawer = () => {
    setDraftFilters(copyFilters(appliedFilters, operationScoped));
    setDrawerOpen(true);
  };

  const updateDraftFilter = (
    field: (typeof FILTER_FIELDS)[number]['name'],
    value: string[],
  ) => {
    setDraftFilters((current) => ({ ...current, [field]: value }));
  };

  const applyFilters = () => {
    if (hasInvalidDateRange) return;

    onApplyFilters(copyFilters(draftFilters, operationScoped));
    setDrawerOpen(false);
  };

  const clearAll = () => {
    setDraftFilters(copyFilters(EMPTY_TIMELINE_FILTERS));
    onClearAll();
  };

  return (
    <>
      <Stack
        data-testid="timeline-toolbar-controls"
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}>
        <TextField
          fullWidth
          size="small"
          label="Buscar na Linha do Tempo"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
          <InputLabel id="timeline-sort-label">Ordenar por</InputLabel>
          <Select
            labelId="timeline-sort-label"
            label="Ordenar por"
            value={sort}
            onChange={(event) =>
              onSortChange(event.target.value as TimelineSortOption)
            }>
            <MenuItem value="materiality">Mais relevantes</MenuItem>
            <MenuItem value="recent">Mais recentes</MenuItem>
            <MenuItem value="oldest">
              {activeTab === 'future' ? 'Mais próximos' : 'Mais antigos'}
            </MenuItem>
          </Select>
        </FormControl>
        <Badge
          badgeContent={countSelectedFilters(appliedFilters, operationScoped)}
          color="primary">
          <Button
            variant="outlined"
            startIcon={<Tune />}
            onClick={openDrawer}
            sx={{ minHeight: 40, minWidth: { xs: '100%', md: 132 } }}>
            Filtros
          </Button>
        </Badge>
      </Stack>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 420 },
            maxWidth: '100vw',
            top: { xs: mobileNavbarHeight, sm: largerNavbarHeight },
            bottom: 0,
            height: 'auto',
            maxHeight: {
              xs: `calc(100dvh - ${mobileNavbarHeight}px)`,
              sm: `calc(100dvh - ${largerNavbarHeight}px)`,
            },
            overflow: 'hidden',
          },
        }}>
        <Stack height="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ p: 3, pb: 2, flexShrink: 0 }}>
            <Box>
              <Typography component="h2" variant="h6">
                Filtros
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Refine os registros por documento, assunto, relevância e data.
              </Typography>
            </Box>
            <IconButton
              aria-label="Fechar filtros"
              onClick={() => setDrawerOpen(false)}
              color="inherit">
              <CloseRounded />
            </IconButton>
          </Stack>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
          <Stack
            data-testid="timeline-filter-fields"
            spacing={2.25}
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
            {filterOptionsError && (
              <Stack spacing={1}>
                <Typography color="error" variant="body2" role="alert">
                  Não foi possível carregar as opções de filtro.
                </Typography>
                <Button onClick={onRetryFilterOptions} size="small">
                  Tentar novamente
                </Button>
              </Stack>
            )}
            {visibleFilterFields.map(({ label, name }) => (
              <Autocomplete<string, true, false, false>
                id={`timeline-filter-${name}`}
                key={name}
                multiple
                disableCloseOnSelect
                disabled={filterOptionsLoading || filterOptionsError}
                loading={filterOptionsLoading}
                options={filterOptions?.[name] ?? []}
                value={draftFilters[name]}
                onChange={(_, value) => updateDraftFilter(name, value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    placeholder="Selecionar"
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': label,
                    }}
                  />
                )}
                size="small"
              />
            ))}
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Data inicial"
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  dateStart: event.target.value,
                }))
              }
              size="small"
              type="date"
              value={draftFilters.dateStart}
              error={hasInvalidDateRange}
              helperText={
                hasInvalidDateRange
                  ? 'A data inicial deve ser anterior à final.'
                  : undefined
              }
            />
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Data final"
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  dateEnd: event.target.value,
                }))
              }
              size="small"
              type="date"
              value={draftFilters.dateEnd}
              error={hasInvalidDateRange}
            />
            {!operationScoped && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={draftFilters.favorites}
                      disabled={!canFilterFavorites}
                      onChange={(event) =>
                        setDraftFilters((current) => ({
                          ...current,
                          favorites: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Somente favoritos"
                />
                {!canFilterFavorites && (
                  <Typography color="text.secondary" variant="caption">
                    Entre na sua conta para filtrar favoritos.
                  </Typography>
                )}
              </>
            )}
          </Stack>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ p: 3, pt: 2, bgcolor: 'background.paper', flexShrink: 0 }}>
            <Button
              disabled={clearIsDisabled}
              fullWidth
              onClick={clearAll}
              startIcon={<CloseRounded />}
              variant="outlined">
              Limpar filtros
            </Button>
            <Button
              disabled={hasInvalidDateRange}
              fullWidth
              onClick={applyFilters}
              variant="contained">
              Aplicar filtros
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}
